import React, { useCallback, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ingredientService } from '../../services/ingredientService';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DeleteConfirmModal from '../../components/UI/DeleteConfirmModal';
import IngredientFamilyModal from '../../components/Setup/IngredientFamilyModal';
import { PlusIcon, MagnifyingGlassIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { IngredientFamily } from '../../types/ingredient';
import { useToast } from '../../components/UI/ToastContainer';
import { Dialog, Transition } from '@headlessui/react';
import DataGrid from '../../components/DataGrid';
import { ColumnDef } from '@tanstack/react-table';

const IngredientFamiliesPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState<{ column: string; order: 'asc' | 'desc' }>({ column: 'name', order: 'asc' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState<IngredientFamily | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<IngredientFamily | null>(null);
  const [reassignSource, setReassignSource] = useState<IngredientFamily | null>(null);
  const [targetFamilyId, setTargetFamilyId] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<{ status?: 'ACTIVE' | 'INACTIVE' }>({});
  const pageSize = 50;
  const queryClient = useQueryClient();
  const { column: sortBy, order: sortOrder } = sort;

  const { data, isLoading, error } = useQuery({
    queryKey: ['ingredient-families', currentPage, searchTerm, sortBy, sortOrder, filters],
    queryFn: () => ingredientService.getIngredientFamilies({ page: currentPage, limit: pageSize, search: searchTerm, sortBy, sortOrder, status: filters.status }),
  });

  const { showError, showSuccess } = useToast();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ingredientService.deleteIngredientFamily(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredient-families'] });
      setConfirmDelete(null);
      showSuccess('Ingredient family deleted', 'The ingredient family has been successfully deleted.');
    },
    onError: (err: any) => {
      const message = err?.response?.data?.message || err?.message || 'Cannot delete ingredient family.';
      console.error('Delete failed', err);
      showError('Delete failed', message);
    },
  });

  const familiesAllQuery = useQuery({
    queryKey: ['ingredient-families', 'all'],
    queryFn: () => ingredientService.getIngredientFamiliesAll(),
    enabled: !!reassignSource,
  });

  const mergeMutation = useMutation({
    mutationFn: (vars: { sourceId: string; targetFamilyId: string }) =>
      ingredientService.mergeIngredientFamily(vars.sourceId, vars.targetFamilyId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['ingredient-families'] });
      setReassignSource(null);
      setTargetFamilyId('');
      showSuccess('Ingredient family deleted', res?.message || 'Reassigned and deleted.');
    },
    onError: (err: any) => {
      const message = err?.response?.data?.message || err?.message || 'Reassign and delete failed.';
      console.error('Merge failed', err);
      showError('Delete failed', message);
    },
  });

  const handleExport = useCallback(async () => {
    try {
      const blob = await ingredientService.exportIngredientFamilies({
        search: searchTerm,
        status: filters.status,
        ids: selectedIds.length ? selectedIds : undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `ingredient-families-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed', e);
    }
  }, [filters.status, searchTerm, selectedIds]);

  const toggleRow = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const handleBulkAction = useCallback(async (status: 'ACTIVE' | 'INACTIVE') => {
    if (!selectedIds.length) return;
    try {
      await ingredientService.bulkUpdateIngredientFamilyStatus(selectedIds, status);
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ['ingredient-families'] });
    } catch (e) {
      console.error('Bulk update failed', e);
    }
  }, [queryClient, selectedIds]);


  const openCreate = useCallback(() => { setSelected(null); setIsModalOpen(true); }, []);
  const openEdit = useCallback((fam: IngredientFamily) => { setSelected(fam); setIsModalOpen(true); }, []);

  const families = data?.ingredientFamilies || [];
  const pagination = data?.pagination;

  const columns = useMemo<ColumnDef<IngredientFamily & { _count?: { ingredients?: number } }>[]>(() => [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      meta: { sortable: true },
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: 'Description',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      id: 'ingredients',
      accessorKey: '_count.ingredients',
      header: 'Ingredients',
      cell: (info) => {
        const value = info.getValue<number>();
        return value !== undefined ? value : 0;
      },
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      meta: { sortable: true },
      cell: ({ row }) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.original.status === 'ACTIVE'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {row.original.status}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const hasIngredients = (row.original as any)._count?.ingredients > 0;
        return (
          <div className="flex items-center justify-end space-x-2">
            <button
              onClick={() => openEdit(row.original)}
              className="text-primary-600 hover:text-primary-900"
              aria-label="Edit"
            >
              <PencilSquareIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                if (hasIngredients) {
                  setReassignSource(row.original);
                  setTargetFamilyId('');
                } else {
                  setConfirmDelete(row.original);
                }
              }}
              className="text-red-600 hover:text-red-900"
              title={hasIngredients ? 'Reassign ingredients before delete' : 'Delete'}
              aria-label={hasIngredients ? 'Reassign ingredients before delete' : 'Delete'}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        );
      },
    },
  ], [openEdit]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">Error loading ingredient families.</p>
      </div>
    );
  }

  const handleSortChange = (columnId: string) => {
    setSort((prev) => {
      if (prev.column === columnId) {
        return { column: columnId, order: prev.order === 'asc' ? 'desc' : 'asc' };
      }
      return { column: columnId, order: 'asc' };
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Ingredient Families</h1>
        <div className="flex space-x-3">
          <Button variant="secondary" onClick={handleExport}>Export</Button>
          <Button onClick={openCreate}><PlusIcon className="w-5 h-5 mr-2" />Create</Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-soft p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input type="text" placeholder="Search families..." className="pl-10 form-input" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
        </div>
      </div>


      {selectedIds.length > 0 && (
        <div className="bg-white rounded-lg shadow-soft p-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">{selectedIds.length} selected</div>
          <div className="flex space-x-2">
            <Button onClick={() => handleBulkAction('ACTIVE')}>Activate</Button>
            <Button variant="danger" onClick={() => handleBulkAction('INACTIVE')}>Deactivate</Button>
            <Button variant="secondary" onClick={() => setSelectedIds([])}>Clear selection</Button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-soft">
        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-3" />
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select
                className="form-input w-full"
                value={filters.status || ''}
                onChange={(e) => {
                  setFilters({ status: (e.target.value as 'ACTIVE' | 'INACTIVE' | '') || undefined });
                  setCurrentPage(1);
                }}
              >
                <option value="">All</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12"><LoadingSpinner size="lg" /></div>
          ) : (
            <DataGrid
              data={families}
              columns={columns}
              getRowId={(row) => row.id}
              enableSelection
              selectedIds={selectedIds}
              onToggleRow={(id) => toggleRow(id)}
              onToggleAll={(ids, checked) =>
                setSelectedIds((prev) =>
                  checked ? Array.from(new Set([...prev, ...ids])) : prev.filter((id) => !ids.includes(id))
                )
              }
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
            />
          )}
        </div>

        {pagination && (
          <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to <span className="font-medium">{Math.min(currentPage * pageSize, pagination.total)}</span> of <span className="font-medium">{pagination.total}</span> results
            </p>
            <div className="space-x-2">
              <Button variant="secondary" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>Previous</Button>
              <Button variant="secondary" disabled={pagination && currentPage === pagination.pages} onClick={() => setCurrentPage((p) => (pagination ? Math.min(pagination.pages, p + 1) : p + 1))}>Next</Button>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <IngredientFamilyModal
          family={selected}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => { setIsModalOpen(false); queryClient.invalidateQueries({ queryKey: ['ingredient-families'] }); }}
        />
      )}

      {reassignSource && (
        <Transition appear show={true} as={React.Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setReassignSource(null)}>
            <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                  <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Reassign and Delete Ingredient Family
                    </Dialog.Title>
                    <div className="mt-3 text-sm text-gray-600">
                      <p className="mb-3">The family "{reassignSource.name}" has {(reassignSource as any)._count?.ingredients ?? 0} ingredients. Choose a target family to move them to, then delete the source family.</p>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Target family</label>
                      <select
                        className="form-select w-full"
                        value={targetFamilyId}
                        onChange={(e) => setTargetFamilyId(e.target.value)}
                      >
                        <option value="">Select a family...</option>
                        {familiesAllQuery.data?.ingredientFamilies
                          ?.filter((f) => f.id !== reassignSource.id)
                          ?.map((f) => (
                            <option key={f.id} value={f.id}>{f.name}</option>
                          ))}
                      </select>
                      {familiesAllQuery.isLoading && <div className="text-xs text-gray-500 mt-1">Loading families...</div>}
                      {!familiesAllQuery.isLoading && (familiesAllQuery.data?.ingredientFamilies?.filter(f => f.id !== reassignSource.id).length ?? 0) === 0 && (
                        <div className="text-xs text-amber-600 mt-2">No other families available. Create another family first to reassign.</div>
                      )}
                    </div>
                    <div className="mt-6 flex justify-end space-x-3">
                      <Button variant="secondary" onClick={() => setReassignSource(null)} disabled={mergeMutation.isPending}>Cancel</Button>
                      <Button
                        variant="danger"
                        onClick={() => mergeMutation.mutate({ sourceId: reassignSource.id, targetFamilyId })}
                        loading={mergeMutation.isPending}
                        disabled={!targetFamilyId || targetFamilyId === reassignSource.id}
                      >
                        Reassign & Delete
                      </Button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      )}


      {confirmDelete && (
        <DeleteConfirmModal
          title="Delete Ingredient Family"
          message={`Are you sure you want to delete "${confirmDelete.name}"? This action cannot be undone.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => deleteMutation.mutate(confirmDelete.id)}
          loading={deleteMutation.isPending}
        />
      )}
    </div>
  );
};

export default IngredientFamiliesPage;
